import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, CheckCircle2, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Icon, SectionHeading } from "../components/Shared";
import PartnerMarquee from "../components/PartnerMarquee";
import ConsultationForm from "../components/ConsultationForm";
import {
  company, images, valueProps, features, serviceCards, benefits, tem,
} from "../mock/mock";

const Home = () => {
  useEffect(() => {
    document.title = "Business VoIP Phone Systems & Consulting | Morrisville Communications";
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden brand-gradient-soft">
        <div className="hero-blob bg-blue-300 h-80 w-80 -top-20 -right-10" />
        <div className="hero-blob bg-sky-200 h-72 w-72 top-40 -left-16" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-up">
              <span className="inline-flex items-center gap-2 bg-white text-blue-700 text-xs font-bold px-4 py-1.5 rounded-full shadow-sm mb-6">
                <Star className="h-3.5 w-3.5 fill-blue-700" /> Not a reseller - your strategic technology partner
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight">
                End-to-End & Hassle-Free Support for Your{" "}
                <span className="text-gradient">Business VoIP Phone System</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl">
                Choose from a wide range of 37 VoIP providers with a financially backed 99.999% uptime SLA. Get up to 3 free months and partner discounts of 15%-40% - all at{" "}
                <span className="font-semibold text-slate-800">no cost to you.</span>
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="brand-gradient text-white rounded-full h-12 px-7 text-base font-semibold w-full sm:w-auto">
                    Talk with a Technology Advisor <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${company.phoneTel}`}>
                  <Button variant="outline" className="rounded-full h-12 px-7 text-base font-semibold border-slate-300 w-full sm:w-auto">
                    <Phone className="h-4 w-4 mr-2" /> {company.phone}
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                {["Zero downtime switch", "No consulting fees", "Lifetime secondary support"].map((t) => (
                  <span key={t} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-600" /> {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative fade-up">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/20 ring-1 ring-white/50">
                <img src={images.hero} alt="Business team collaborating on communications" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 hidden sm:block">
                <div className="text-3xl font-extrabold text-blue-700">380+</div>
                <div className="text-sm text-slate-600">IT leaders trust us</div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 hidden sm:block">
                <div className="text-2xl font-extrabold text-blue-700">99.999%</div>
                <div className="text-xs text-slate-600">Uptime SLA</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <p className="text-center text-sm font-semibold tracking-widest uppercase text-slate-400">
            Proudly working with
          </p>
        </div>
        <PartnerMarquee />
      </section>

      {/* NOT A RESELLER */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl order-2 lg:order-1">
            <img src={images.consulting} alt="Consulting meeting" className="w-full h-[380px] object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="A strategic sales partner"
              title="Not a reseller - your strategic partner"
            />
            <p className="mt-5 text-slate-600 text-lg">
              Morrisville Communications LLC is a strategic partner company for leading VoIP phone system providers. With our partnership you get access to pricing - including equipment pricing - you would not find elsewhere.
            </p>
            <p className="mt-4 text-slate-600 text-lg">
              You also get access to our tier 2 and tier 3 support without having to deal with tier 1, so issues get resolved faster and more efficiently.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-6 font-semibold text-blue-700 hover:gap-3 transition-all">
              Get your no-cost consultation today <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-20 brand-gradient-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="About Morrisville Communications" title="Reliability you can build your business on" />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {valueProps.map((v) => (
              <Card key={v.title} className="card-hover p-8 rounded-2xl border-slate-100 bg-white">
                <div className="h-12 w-12 rounded-xl brand-gradient flex items-center justify-center mb-5">
                  <Icon name={v.icon} className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-slate-600">{v.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Business Phone Services" title="Everything your team needs to communicate" />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-5">
            {features.map((f) => (
              <div key={f.title} className="card-hover flex items-center gap-4 p-6 rounded-2xl border border-slate-100 bg-slate-50">
                <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                  <Icon name={f.icon} className="h-5 w-5 text-blue-700" />
                </div>
                <span className="font-semibold text-slate-800">{f.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div className="lg:sticky lg:top-28">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-400">What's in it for you?</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 leading-tight">
              How do you benefit from our services?
            </h2>
            <p className="mt-5 text-slate-300 text-lg">
              No-cost consultation, reliable & experienced engineers for hosted VoIP solutions.
            </p>
            <div className="mt-8 space-y-3">
              <a href={`tel:${company.phoneTel}`} className="flex items-center gap-3 text-lg font-semibold hover:text-sky-400">
                <Phone className="h-5 w-5 text-sky-400" /> {company.phone}
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-3 text-lg font-semibold hover:text-sky-400 break-all">
                <Mail className="h-5 w-5 text-sky-400" /> {company.email}
              </a>
            </div>
          </div>
          <div className="grid gap-3">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3 bg-slate-800/60 rounded-xl p-4 hover:bg-slate-800 transition-colors">
                <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
                <span className="text-slate-200">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="For small to mid-size business" title="Affordable, fully supported telephone services" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCards.map((s) => (
              <Card key={s.title} className="card-hover p-7 rounded-2xl border-slate-100">
                <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Icon name={s.icon} className="h-5 w-5 text-blue-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TELECOM EXPENSE MANAGEMENT */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-400">
                {tem.eyebrow}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-3 leading-tight">{tem.title}</h2>
              <p className="mt-5 text-lg text-slate-300">{tem.intro}</p>
              <div className="mt-6 space-y-3">
                {tem.benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
                    <span className="text-slate-200">{b}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="mt-8 brand-gradient text-white rounded-full h-12 px-7 font-semibold">
                  Get a free telecom bill review <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {tem.features.map((f) => (
                <div
                  key={f.title}
                  className="rounded-2xl bg-slate-800/60 p-6 hover:bg-slate-800 transition-colors border border-slate-700/50"
                >
                  <div className="h-11 w-11 rounded-xl bg-sky-500/15 flex items-center justify-center mb-4">
                    <Icon name={f.icon} className="h-5 w-5 text-sky-400" />
                  </div>
                  <h3 className="font-bold text-white mb-1.5">{f.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONSULTATION */}
      <section className="py-20 brand-gradient-soft" id="consult">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading
              eyebrow="Trusted by 380+ IT leaders"
              title="Book your no-cost, no-obligation consultation"
              subtitle="We provide detailed information on user interface, admin interface & pricing for all requested providers. Book a call to access professional IT consulting."
            />
            <img src={images.customerService} alt="Customer support representative" className="mt-8 rounded-2xl shadow-xl w-full h-64 object-cover hidden lg:block" />
          </div>
          <Card className="p-8 rounded-3xl shadow-2xl border-slate-100 bg-white">
            <ConsultationForm />
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;
